import { Callout, Tooltip, ArrowIcon, css, styled, List, ListItem, Mode, ArrowUpIcon } from 'canoe';
import React, { useRef, useState } from 'react';

interface Item {
  name: string;
  value: string;
}

interface VariantPickerProps {
  selectedVariant?: string;
  items: Item[];
  onSelectVariant: (selectedValue: string) => void;
  tooltipContent?: React.ReactNode;
  mode?: Mode;
}

const VariantTrigger = styled.button<{ isOpen: boolean; mode?: Mode }>`
  ${({ theme, isOpen, mode }) => css`
    background-color: ${theme.colors.Transparent};
    border-radius: ${theme.borderRadius.small};
    border: none;
    box-shadow: none;
    max-width: 344px;
    padding: 0 ${theme.spaces.xs};
    display: flex;
    outline: none;
    align-items: center;
    cursor: pointer;
    transition: ${theme.transitions.default()};

    ${Title} {
      color: ${mode === 'Dark' ? theme.colors.White : theme.colors.OnyxGrayD10};
    }
    &:active,
    &:focus,
    &:focus-within {
      outline: none;
    }

    &:hover,
    &:focus,
    &:active {
      background-color: ${theme.colors.White};
      ${Title} {
        color: ${mode === 'Dark' ? theme.colors.OnyxGrayD10 : theme.colors.White};
      }

      svg {
        polygon,
        path {
          fill: ${mode === 'Dark' ? theme.colors.OnyxGrayD10 : theme.colors.White};
        }

        rect,
        circle {
          stroke: ${mode === 'Dark' ? theme.colors.OnyxGrayD10 : theme.colors.White};
        }
      }
    }

    ${isOpen &&
    css`
      background-color: ${theme.colors.White};
      ${Title} {
        color: ${mode === 'Dark' ? theme.colors.OnyxGrayD10 : theme.colors.White};
      }
    `}
  `}
`;

const ListWrapper = styled.div`
  position: relative;
  z-index: 10001;
  min-width: 200px;
`;

const Title = styled.p`
  ${({ theme }) => css`
    line-height: ${theme.fonts.body.lineHeight};
    margin: 0;
    text-align: left;
    flex: 1;
  `}
`;

export const VariantPicker = ({
  selectedVariant,
  items,
  onSelectVariant,
  mode,
  tooltipContent,
}: VariantPickerProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const onSelect = (selected: Item | null) => {
    onSelectVariant(selected?.value ?? '');
    setIsOpen(false);
  };

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <>
      {isOpen && (
        <Callout
          usePortal={false}
          referenceElement={triggerRef.current}
          onClose={() => setIsOpen(false)}
          closeKey="Escape"
          placement="bottom"
        >
          {({ ref, style, attributes }) => (
            <ListWrapper data-craft-ignore-events ref={ref} style={style} {...attributes}>
              <List<Item>
                scale="small"
                onSelect={onSelect}
                items={items}
                renderItem={({ item, ...props }) => (
                  <ListItem
                    {...props}
                    isSelected={item.value === selectedVariant}
                    mode="Dark"
                    key={`dropdown-option-${item.value}`}
                  >
                    {item.name}
                  </ListItem>
                )}
              />
            </ListWrapper>
          )}
        </Callout>
      )}
      <Tooltip placement="top" offset="xxs" content={tooltipContent}>
        <VariantTrigger mode={mode} isOpen={isOpen} onClick={toggleOpen} ref={triggerRef}>
          <Title>{selectedVariant}</Title>
          {isOpen ? (
            <ArrowUpIcon ml="xs" color={mode === 'Dark' ? 'OnyxGrayD10' : 'White'} />
          ) : (
            <ArrowIcon ml="xs" color={mode === 'Dark' ? 'White' : 'OnyxGrayD10'} />
          )}
        </VariantTrigger>
      </Tooltip>
    </>
  );
};
